/**
 * Weather warnings, on the profile: how the weather ahead reaches the
 * gardener, in two parts, each saying only what it is about.
 *
 * Warnings by email or text first: when one comes (at the hour the gardener
 * chose, or the Grow settings' send hour, warnings.SEND_HOUR, on their own
 * clock, on days the weather a sending rule is for is due tonight or
 * tomorrow; at most one a day), what it says (a card as the email words it),
 * and what is mentioned too, all as the server reads them from its rules
 * (AlertPreferences.sends; audit batch 9b), then the hour ("Send my warnings": 7 AM, 12 PM, 2 PM or
 * 4 PM, shown once the server can keep it), email (on by default), a text
 * where it is on offer and there is a number, and app notifications on this
 * device. A button sends today's message now, or a sample on a quiet day.
 *
 * Notifications ("Turn on notifications") are offered only where they work
 * (shared/lib/pushNotifications): a browser with a service worker and the
 * Push API, and a server with its keys. An iPhone or iPad not opened from
 * the home screen is told that notifications need Grow there, and how; a
 * browser that cannot show them, or that has blocked them for Grow, says so,
 * with no switch. Permission is asked only when the switch is tapped. Each
 * device is its own: the switch is about this one.
 *
 * How many days ahead Today's Coming up looks was a second part here (On
 * Today); it is a row of its own on the profile now (ComingUpScreen), where
 * somebody looking for it would look.
 *
 * It opened with the Coming up choice, under a title about warnings; said
 * when only as "late afternoon" on a day "about to hurt something"; listed
 * "Default (3 days)" and "3 days" as two choices; and pointed "above" to a
 * postcode that is asked on Today now.
 *
 * Each is a choice, so each is kept the moment it changes, with "Saved"
 * beside it; the switches used to save in silence.
 *
 * Used by: UserProfile (?s=warnings). The send hour comes from the
 * gardener's preferences and the owner's Grow settings, and what sends one
 * from the server's rules (shared/api/alerts), so this screen says whatever
 * the backend will actually do; with no rule sending, it says so.
 *
 * A read, a save or a send that fails on our side (a 5xx or no answer) says
 * the standard sentence, reported to Sentry, with the support address as a
 * link and no "Try again" (D31); other failures keep their own words. A
 * browser that will not subscribe for notifications is a failure on our side
 * too: reported to Sentry, and the standard sentence.
 *
 * @module WarningsCard
 */

import React from 'react';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Switch } from '@/shared/components/ui/switch';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { useToast } from '@/shared/components/ui/use-toast';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getAlertPreferences, sendWarningNow, setAlertPreferences, setWarningHour } from '@/shared/api/alerts';
import { getPushPublicKey } from '@/shared/api/push';
import {
  askPermission, deviceSubscribed, notificationPermission, pushSupport, turnOffPush, turnOnPush,
  type PushSupport,
} from '@/shared/lib/pushNotifications';
import { ProfileScreen } from './ProfileScreen';
import { Segmented, SettingList, SettingRow } from './profileKit';
import { useSavedTicks } from './useSavedTicks';
import { DateTime } from '@/shared/utils/dateUtils';
import { Link } from 'react-router-dom';

import { ALERT_PREFERENCES_KEY } from './profileQueries';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure, serverFailureWords, rateLimitedText, reportOtherFailure } from '@/shared/lib/serverFailure';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';

/** A small heading over one part of the screen. */
const PART = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** Where this device's notification state is cached: it is the device's, not the account's. */
const PUSH_DEVICE_KEY = ['push', 'device'] as const;

/** What the notifications row needs to know about this device and the server. */
interface PushDevice {
  /** Whether notifications can work here, and if not, why. */
  support: PushSupport;
  /** The server's key to subscribe with; null when it cannot send notifications. */
  publicKey: string | null;
  /** The browser's permission for Grow. */
  permission: NotificationPermission;
  /** Whether notifications are on on this device. */
  subscribed: boolean;
}

/**
 * This device's notification state: what it supports, the server's key,
 * the permission, and whether it is subscribed. The server is asked only
 * where notifications could work.
 *
 * @returns The state.
 * @throws A failed request for the server's key.
 */
async function readPushDevice(): Promise<PushDevice> {
  const support = await pushSupport();
  if (support !== 'ok') return { support, publicKey: null, permission: notificationPermission(), subscribed: false };
  const [publicKey, subscribed] = await Promise.all([getPushPublicKey(), deviceSubscribed()]);
  return { support, publicKey, permission: notificationPermission(), subscribed };
}

/** The weather warnings screen: the hour, email, text and notifications. */
export const WarningsScreen: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // A read that failed is said, rather than "Loading" for ever.
  const { data, isLoading, isError, error: loadError } = useQuery({ queryKey: ALERT_PREFERENCES_KEY, queryFn: getAlertPreferences, enabled: open });
  const push = useQuery({ queryKey: PUSH_DEVICE_KEY, queryFn: readPushDevice, enabled: open, retry: false });
  const { saved, flash } = useSavedTicks();
  const [error, setError] = React.useState<string | null>(null);
  const emailId = React.useId();
  const smsId = React.useId();
  const pushId = React.useId();

  /**
   * An hour in the reader's clock: "4 PM", "16 h".
   *
   * @param value - 0–23.
   * @returns The hour as the reader writes it.
   */
  const hourWords = (value: number): string => DateTime.fromObject({ hour: value }).setLocale(language).toLocaleString({ hour: 'numeric' });
  /** The hour theirs goes out: the one they chose, or the server's (the Grow setting warnings.SEND_HOUR, sent as defaultSendHour); null until the read is in. */
  const sendHour = data ? (data.sendHour ?? data.defaultSendHour) : null;
  /** The send hour in the reader's clock. */
  const hour = sendHour === null ? '' : hourWords(sendHour);
  /**
   * Names as a sentence lists them: "a freeze, frost, cold or heat".
   *
   * @param names - In the server's order.
   * @returns The names joined with the reader's "or"; empty for none.
   */
  const anyOf = (names: string[]): string => (names.length > 1
    ? `${names.slice(0, -1).join(', ')}${t('profile.warnings.or')}${names[names.length - 1]}`
    : names[0] ?? '');
  /** What sends one, in the server's words from its rules: "a freeze, frost, cold, heat, strong wind or hail". */
  const conditions = anyOf(data?.sends?.conditions ?? []);
  /** What is mentioned too when a warning goes out anyway: "a chilly night, a cold snap or a downpour". */
  const along = anyOf(data?.sends?.along ?? []);
  /** A warning's card as it reads, from the first rule that sends one: "Cover 2 plants · Tonight". */
  const sample = data?.sends?.sample ?? '';
  /** Keeps the email and text switches, ticking "Saved" beside the one changed. */
  const save = useMutation({
    mutationFn: ({ byEmail, bySms }: { byEmail: boolean; bySms: boolean; key: string }) => setAlertPreferences(byEmail, bySms),
    onMutate: () => setError(null),
    onSuccess: (next, { key }) => { queryClient.setQueryData(ALERT_PREFERENCES_KEY, next); flash(key); },
    onError: (err) => setError(errorMessage(err, t('profile.warnings.saveFailed'))),
  });
  /** Keeps the hour chosen, ticking "Saved" beside it. */
  const saveHour = useMutation({
    mutationFn: (next: number) => setWarningHour(next),
    onMutate: () => setError(null),
    onSuccess: (next) => { queryClient.setQueryData(ALERT_PREFERENCES_KEY, next); flash('hour'); },
    onError: (err) => setError(errorMessage(err, t('profile.warnings.saveFailed'))),
  });
  /**
   * What to say when notifications could not be turned on or off: a request's
   * failure as any other, and the browser's refusal to subscribe as a failure
   * on our side, reported.
   *
   * @param err - What was thrown.
   * @returns The sentence.
   */
  const pushFailure = (err: unknown): string => {
    if (axios.isAxiosError(err)) return errorMessage(err, t('profile.warnings.pushFailed'));
    reportOtherFailure(err, 'WarningsCard notifications');
    return serverFailureWords(err);
  };
  /** Turns notifications on (permission already asked from the tap) or off on this device. */
  const savePush = useMutation({
    mutationFn: async ({ on, asked }: { on: boolean; asked?: Promise<NotificationPermission> }) => {
      if (!on) {
        await turnOffPush();
        return 'off' as const;
      }
      const publicKey = push.data?.publicKey;
      if (!publicKey || !asked) throw new Error('Notifications were turned on with no server key');
      return turnOnPush(publicKey, asked);
    },
    onMutate: () => setError(null),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: PUSH_DEVICE_KEY });
      if (result !== 'denied') flash('push');
    },
    onError: (err) => {
      void queryClient.invalidateQueries({ queryKey: PUSH_DEVICE_KEY });
      setError(pushFailure(err));
    },
  });
  /** Sends today's warning now (a sample on a quiet day), and says what happened. */
  const sendNow = useMutation({
    mutationFn: sendWarningNow,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ALERT_PREFERENCES_KEY });
      if (result.sent) {
        toast({
          title: result.sample ? t('profile.warnings.sentSample') : t('profile.warnings.sentToday'),
          description: result.warning?.headline,
        });
        return;
      }
      /*
       * Nothing arrived, and why matters.
       *
       * A quiet day is the garden being fine. A message that was written and
       * then refused is a broken setting, and calling that "nothing to warn
       * about" sent somebody looking at the forecast over a missing mail key.
       */
      const quiet = result.reason === 'QUIET' || result.reason === null;
      toast({
        title: quiet ? t('profile.warnings.notSent') : t('profile.warnings.mailerOff'),
        description: quiet ? result.warning?.headline : t('profile.warnings.mailerOffHint'),
        variant: quiet ? undefined : 'destructive',
      });
    },
    onError: (err) => toast({ title: errorMessage(err, t('profile.warnings.sendFailed')), variant: 'destructive' }),
  });

  /** Whether the notifications switch is offered here: it works, the server can send, and Grow is not blocked. */
  const pushOffered = push.data?.support === 'ok' && Boolean(push.data.publicKey) && push.data.permission !== 'denied';
  /** The line under "Turn on notifications": what it does, or why it is not offered here. */
  const pushHint: React.ReactNode = push.isError
    ? <FailureText text={errorMessage(push.error, t('sitewide.loadFailed'))} />
    : push.data?.support === 'ios-home-screen'
      ? t('profile.warnings.pushIphone')
      : push.data?.support === 'unsupported'
        ? t('profile.warnings.pushUnsupported')
        : !push.data?.publicKey
          ? t('profile.warnings.pushSoon')
          : push.data.permission === 'denied'
            ? t('profile.warnings.pushBlocked')
            : t('profile.warnings.pushHint');

  return (
    <ProfileScreen open={open} title={t('profile.warnings.title')} onClose={onClose}>
      {!data && isError ? (
        isServerFailure(loadError)
          ? <ServerFailureNote error={loadError} where="WarningsCard" />
          : <p role="alert" className="text-sm text-destructive"><FailureText text={rateLimitedText(loadError) ?? t('sitewide.loadFailed')} /></p>
      ) : isLoading || !data ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner size={16} />{t('sitewide.loading')}</div>
      ) : (
        <>
          <section className="space-y-3" aria-labelledby="warnings-by-message">
            <h3 id="warnings-by-message" className={PART}>{t('profile.warnings.byMessage')}</h3>
            <p className="text-sm">{conditions ? t('profile.warnings.when', { hour, conditions }) : t('profile.warnings.noneSend')}</p>
            {sample && <p className="text-sm text-muted-foreground">{t('profile.warnings.what', { sample })}</p>}
            {conditions && along && <p className="text-sm text-muted-foreground">{t('profile.warnings.along', { along })}</p>}
            {!data.hasPostalCode && (
              <p className="rounded-md border border-warning/40 bg-warning-tint px-3 py-2 text-sm text-warning">
                {t('profile.warnings.needsPostalCode')}{' '}
                {/* Today is where the postcode is asked for. */}
                <Link to="/today" onClick={onClose} className="font-medium underline underline-offset-2">
                  {t('profile.warnings.addPostalCode')}
                </Link>
              </p>
            )}
            <SettingList>
              {/* The hour, from the server's choices. */}
              {data.hourChoices.length > 0 && (
                <SettingRow title={t('profile.warnings.hourTitle')} hint={t('profile.warnings.hourHint')} saved={saved('hour')} stacked>
                  <Segmented
                    value={String(sendHour ?? '')}
                    options={data.hourChoices.map((choice) => ({ value: String(choice), label: hourWords(choice) }))}
                    onChange={(next) => saveHour.mutate(Number(next))}
                    label={t('profile.warnings.hourTitle')}
                    disabled={saveHour.isPending}
                  />
                </SettingRow>
              )}
              <SettingRow title={t('profile.warnings.email')} hint={t('profile.warnings.emailHint')} saved={saved('email')} htmlFor={emailId}>
                <Switch
                  id={emailId}
                  checked={data.byEmail}
                  disabled={save.isPending}
                  onCheckedChange={(on) => save.mutate({ byEmail: on, bySms: data.bySms, key: 'email' })}
                />
              </SettingRow>
              <SettingRow
                title={t('profile.warnings.sms')}
                hint={!data.smsAvailable
                  ? t('profile.warnings.smsSoon')
                  : !data.hasPhone
                    ? t('profile.warnings.smsNeedsPhone')
                    : t('profile.warnings.smsHint')}
                saved={saved('sms')}
                htmlFor={smsId}
              >
                <Switch
                  id={smsId}
                  checked={data.bySms}
                  disabled={save.isPending || !data.smsAvailable || !data.hasPhone}
                  onCheckedChange={(on) => save.mutate({ byEmail: data.byEmail, bySms: on, key: 'sms' })}
                />
              </SettingRow>
              {/* Notifications on this device: the switch only where they work; otherwise why not. */}
              {(push.data || push.isError) && (
                <SettingRow title={t('profile.warnings.pushTitle')} hint={pushHint} saved={saved('push')} htmlFor={pushOffered ? pushId : undefined}>
                  {pushOffered && (
                    <Switch
                      id={pushId}
                      checked={Boolean(push.data?.subscribed)}
                      disabled={savePush.isPending}
                      // Permission is asked here, in the tap itself, before anything is awaited.
                      onCheckedChange={(on) => savePush.mutate(on ? { on, asked: askPermission() } : { on })}
                    />
                  )}
                </SettingRow>
              )}
            </SettingList>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={() => sendNow.mutate()} disabled={sendNow.isPending || !data.hasPostalCode}>
                {sendNow.isPending ? <Spinner size={16} /> : null}
                <span className={sendNow.isPending ? 'ml-2' : ''}>{t('profile.warnings.sendNow')}</span>
              </Button>
              <p className="text-xs text-muted-foreground">
                {data.lastSentOn ? t('profile.warnings.lastSent', { date: data.lastSentOn }) : t('profile.warnings.neverSent')}
              </p>
            </div>
          </section>

          {error && <p className="text-sm text-destructive"><FailureText text={error} /></p>}
        </>
      )}
    </ProfileScreen>
  );
};
